import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import {
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Unstable_Grid2,
} from '@mui/material';
import { Controller, type FieldErrors, type UseFormReturn, useFieldArray } from 'react-hook-form';
import Alert from '/@/components/bayer/alert';
import { _ } from '/@/locale';
import type { YieldEstimationForm } from '../..';

const YieldEstimationFormset = ({
    setGuideModalIsOpen,
    onCancel,
    goNext,
    onInvalid,
    form,
    weight1000GrainsDefaultValue,
}: {
    setGuideModalIsOpen: (bool: boolean) => void;
    /** Dirty-aware close requested from the parent. */
    onCancel: () => void;
    goNext: () => void;
    /** "Estimar" pressed with samples missing data. */
    onInvalid?: (errors: FieldErrors<YieldEstimationForm>) => void;
    form: UseFormReturn<YieldEstimationForm>;
    weight1000GrainsDefaultValue?: number;
}) => {
    const { control, handleSubmit, formState, watch } = form;
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'samples',
    });

    const { isValid, isSubmitSuccessful, isSubmitted } = formState;

    const addMoreDisabled = fields.length >= 5;

    // "Estimar" only computes and advances to the result preview; nothing is persisted until
    // the user presses "Guardar" (avoids draft rows and duplicate estimations on re-edit).
    const onSubmit = () => {
        if (isValid) goNext();
    };

    return (
        <Stack
            component='form'
            onSubmit={handleSubmit(onSubmit, onInvalid)}
            // Fill the drawer below the header (flex, not `height: 100%`, which overflowed it by the
            // header's height and let focus-scroll push the header out of view on phones).
            sx={{ flex: 1, minHeight: 0 }}
        >
            <Stack p={{ xs: 2, sm: 3 }} spacing={3} overflow={'auto'} flex={1} minHeight={0}>
                <Stack spacing={1}>
                    <Typography color={'primary'} variant='subtitle1' fontWeight={'bold'}>
                        {_('Ingresá los datos de las muestras')}
                    </Typography>
                    <Typography color={'primary.mui.inactive'} variant='body2'>
                        {_(
                            'Asegurate de tomar una o más muestras distribuidas de forma homogénea en el lote. Si tenés diferentes ambientes, agregá muestras para obtener resultados más precisos.'
                        )}
                    </Typography>
                </Stack>
                <Stack
                    direction={'row'}
                    alignItems={'center'}
                    spacing={1}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setGuideModalIsOpen(true)}
                >
                    <HelpOutlinedIcon color={'secondary'} />
                    <Typography color={'secondary'} sx={{ textDecoration: 'underline' }} variant='body2'>
                        {_('Guía rápida para tomar muestras')}
                    </Typography>
                </Stack>
                {!isValid && !isSubmitSuccessful && isSubmitted && (
                    <Alert severity='danger'>
                        {_(
                            'Algunos de los datos ingresados no son correctos o están incompletos. Por favor, revisá los campos para continuar.'
                        )}
                    </Alert>
                )}
                {fields.map((field, index) => (
                    <Stack spacing={2} key={field.id}>
                        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Typography color={'primary'} variant='overline' alignSelf={'center'}>
                                {_('MUESTRA ') + (index + 1)}
                            </Typography>
                            {fields.length > 1 && (
                                <IconButton color='primary' onClick={() => remove(index)} size='small'>
                                    <DeleteOutlineOutlinedIcon />
                                </IconButton>
                            )}
                        </Stack>
                        <Unstable_Grid2 container spacing={2}>
                            <Unstable_Grid2 xs={12} sm={6} pl={0} pr={{ xs: 0, sm: 1 }}>
                                <Controller
                                    name={`samples.${index}.name`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            label={_('Nombre')}
                                            {...field}
                                            error={fieldState.error?.message !== undefined}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                    rules={{
                                        required: _('Campo requerido'),
                                        maxLength: {
                                            value: 50,
                                            message: _('Máximo {{value}} caracteres', { value: 50 }),
                                        },
                                    }}
                                />
                            </Unstable_Grid2>
                            <Unstable_Grid2 xs={12} sm={6} pr={0} pl={{ xs: 0, sm: 1 }}>
                                <Controller
                                    name={`samples.${index}.spikes`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            type='number'
                                            fullWidth
                                            label={_('Espigas/ha')}
                                            {...field}
                                            error={fieldState.error?.message !== undefined}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                    rules={{
                                        required: _('Campo requerido'),
                                        min: {
                                            value: 30000,
                                            message: _('Debe ser mayor a {{value}}', {
                                                value: 30000,
                                            }),
                                        },
                                        max: {
                                            value: 150000,
                                            message: _('Debe ser menor a {{value}}', {
                                                value: 150000,
                                            }),
                                        },
                                    }}
                                />
                            </Unstable_Grid2>
                            <Unstable_Grid2 xs={12} sm={6} pl={0} pr={{ xs: 0, sm: 1 }}>
                                <Controller
                                    name={`samples.${index}.grainsSpike`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            type='number'
                                            fullWidth
                                            label={_('Granos/Espiga')}
                                            {...field}
                                            error={fieldState.error?.message !== undefined}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                    rules={{
                                        required: _('Campo requerido'),
                                        min: {
                                            value: 200,
                                            message: _('Debe ser mayor a {{value}}', { value: 200 }),
                                        },
                                        max: {
                                            value: 1500,
                                            message: _('Debe ser menor a {{value}}', { value: 1500 }),
                                        },
                                    }}
                                />
                            </Unstable_Grid2>
                            <Unstable_Grid2 xs={12} sm={6} pr={0} pl={{ xs: 0, sm: 1 }}>
                                <Controller
                                    name={`samples.${index}.weight1000Grains`}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            type='number'
                                            fullWidth
                                            label={_('Peso de 1000 granos')}
                                            InputProps={{
                                                endAdornment: <InputAdornment position='end'>{_('g')}</InputAdornment>,
                                            }}
                                            {...field}
                                            error={fieldState.error?.message !== undefined}
                                            helperText={fieldState.error?.message}
                                        />
                                    )}
                                    rules={{
                                        required: _('Campo requerido'),
                                        min: {
                                            value: 100,
                                            message: _('Debe ser mayor a {{value}}', { value: 100 }),
                                        },
                                        max: {
                                            value: 500,
                                            message: _('Debe ser menor a {{value}}', { value: 500 }),
                                        },
                                    }}
                                />
                                {Boolean(weight1000GrainsDefaultValue) &&
                                    watch(`samples.${index}.weight1000Grains`) === weight1000GrainsDefaultValue && (
                                        <Typography color={'primary.mui.inactive'} variant='caption'>
                                            {_('Valor obtenido del catálogo del híbrido')}
                                        </Typography>
                                    )}
                            </Unstable_Grid2>
                        </Unstable_Grid2>
                        <Divider />
                    </Stack>
                ))}
                <Tooltip
                    title={
                        addMoreDisabled
                            ? _('Alcanzaste el máximo de 5 muestras. Editá o eliminá una para agregar otra.')
                            : ''
                    }
                    placement='top'
                >
                    <Stack justifyContent={'center'}>
                        <Button
                            onClick={() =>
                                append({
                                    name: '',
                                    spikes: '',
                                    grainsSpike: '',
                                    weight1000Grains: weight1000GrainsDefaultValue || '',
                                })
                            }
                            disabled={addMoreDisabled}
                        >
                            {`+ ${_('Agregar otra muestra')}`}
                        </Button>
                    </Stack>
                </Tooltip>
            </Stack>
            <Stack
                direction={'row'}
                justifyContent={'flex-end'}
                spacing={2}
                p={2}
                borderTop={(t) => `1px solid ${t.palette.divider}`}
            >
                <Button variant='text' color='secondary' onClick={onCancel} sx={{ flex: { xs: 1, sm: 'none' } }}>
                    {_('Cancelar')}
                </Button>
                <Button variant='contained' color='secondary' type='submit' sx={{ flex: { xs: 1, sm: 'none' } }}>
                    {_('Estimar Rendimiento')}
                </Button>
            </Stack>
        </Stack>
    );
};

export default YieldEstimationFormset;
